import React, { useState, useEffect, useRef, useMemo } from "react";
import { sendContactMail } from "./mailService";

// ─────────────────────────────────────────────────────────────
//  GLOBAL CSS  — chalk board aesthetic
// ─────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;800&family=Noto+Sans+Tamil:wght@400;600;700&family=Patrick+Hand&display=swap');

  /* Ensure Noto Sans Tamil is pre-loaded for Tamil unicode range */
  @font-face {
    font-family: 'Noto Sans Tamil';
    font-display: swap;
    unicode-range: U+0B80-0BFF;
  }

  :root {
    --board-green   : #1a3a2a;
    --board-edge    : #2d1e0f;
    --board-shine   : rgba(255,255,255,0.03);
    --chalk-white   : #e8e4d8;
    --chalk-yellow  : #f9e080;
    --chalk-blue    : #7ac8f8;
    --chalk-pink    : #f4a7b9;
    --chalk-green   : #a8d8a8;
    --overlay-bg    : rgba(0, 0, 0, 0.72);
    --overlay-border: rgba(255,255,255,0.13);
    --q-color       : #f9e080;
    --a-color       : #e8e4d8;
    --badge-bg      : rgba(255,255,255,0.12);
    --pill-active   : rgba(255,255,255,0.22);
    --shadow-board  : 0 24px 80px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.5);
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body, html { background: #ffffff; min-height: 100vh; }

  /* ── Top Navigation Bar ── */
  .vks-navbar {
    width: 100%;
    background: rgba(255,255,255,0.97);
    border-bottom: 1px solid rgba(0,0,0,0.08);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 52px;
    box-shadow: 0 1px 8px rgba(0,0,0,0.06);
  }
  .vks-navbar-brand {
    font-family: 'Baloo 2', sans-serif;
    font-size: 16px;
    font-weight: 800;
    color: #a8d8a8;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }
  .vks-navbar-brand span {
    color: #f9e080;
  }
  .vks-navbar-links {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .vks-nav-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 50px;
    padding: 7px 16px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    font-family: 'Baloo 2', sans-serif;
    letter-spacing: 0.3px;
    text-decoration: none;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
  }
  .vks-nav-link-home {
    background: rgba(168,216,168,0.10);
    border: 1.5px solid rgba(168,216,168,0.22);
    color: #a8d8a8;
  }
  .vks-nav-link-home:hover {
    background: rgba(168,216,168,0.22);
    border-color: rgba(168,216,168,0.45);
    color: #c6edc6;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(168,216,168,0.18);
  }
  .vks-nav-link-contact {
    background: rgba(122,200,248,0.10);
    border: 1.5px solid rgba(122,200,248,0.22);
    color: #7ac8f8;
  }
  .vks-nav-link-contact:hover {
    background: rgba(122,200,248,0.22);
    border-color: rgba(122,200,248,0.45);
    color: #a8dafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(122,200,248,0.18);
  }
  @media (max-width: 480px) {
    .vks-navbar { padding: 0 12px; }
    .vks-navbar-brand { font-size: 14px; }
    .vks-nav-link { padding: 6px 12px; font-size: 11px; }
  }

  .vks-root {
    font-family: 'Baloo 2', sans-serif;
    min-height: 100vh;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 12px 100px;
  }

  /* ── Header ── */
  .vks-header {
    width: 100%;
    max-width: 720px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 18px;
  }
  .vks-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .vks-meta-pill {
    background: rgba(0,0,0,0.05);
    border: 1px solid rgba(0,0,0,0.10);
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 50px;
    letter-spacing: 0.3px;
  }
  .vks-chapter-title {
    font-size: 18px;
    font-weight: 800;
    color: #1e293b;
    letter-spacing: 0.3px;
    line-height: 1.3;
  }

  /* ── Topic group pills ── */
  .vks-topic-scroll {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;
    width: 100%;
    max-width: 720px;
    margin-bottom: 4px;
  }
  .vks-topic-scroll::-webkit-scrollbar { display: none; }
  .vks-topic-pill {
    flex-shrink: 0;
    background: rgba(0,0,0,0.04);
    border: 1.5px solid rgba(0,0,0,0.10);
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    padding: 5px 14px;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.18s;
    white-space: nowrap;
    font-family: 'Baloo 2', sans-serif;
  }
  .vks-topic-pill:hover { background: rgba(0,0,0,0.08); color: #1e293b; }
  .vks-topic-pill.active {
    background: var(--board-green);
    border-color: #4ade80;
    color: #a8d8a8;
    box-shadow: 0 0 0 3px rgba(74,222,128,0.15);
  }

  /* ── Lang toggle ── */
  .vks-lang-bar {
    display: flex;
    gap: 6px;
    background: rgba(0,0,0,0.05);
    border-radius: 50px;
    padding: 4px;
    border: 1px solid rgba(0,0,0,0.08);
  }
  .vks-lang-btn {
    border: none;
    border-radius: 50px;
    padding: 5px 16px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s;
    background: transparent;
    color: #64748b;
    font-family: 'Baloo 2', sans-serif;
    letter-spacing: 0.3px;
  }
  .vks-lang-btn.active {
    background: #ffffff;
    color: #1e293b;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  }

  /* ─────────────────────────────────────────────
     THE BOARD
  ───────────────────────────────────────────── */
  .vks-board-wrap {
    width: 100%;
    max-width: 720px;
    position: relative;
  }

  /* wooden frame */
  .vks-board-frame {
    background: linear-gradient(135deg, #4a2c0a 0%, #6b3d12 30%, #3d1f08 70%, #5a3010 100%);
    border-radius: 10px;
    padding: 14px;
    box-shadow: var(--shadow-board);
    position: relative;
  }
  /* wood grain effect */
  .vks-board-frame::before {
    content: '';
    position: absolute; inset: 0;
    border-radius: 10px;
    background: repeating-linear-gradient(
      92deg,
      transparent,
      transparent 40px,
      rgba(0,0,0,0.04) 40px,
      rgba(0,0,0,0.04) 41px
    );
    pointer-events: none;
  }
  /* metal corner screws */
  .vks-board-frame::after {
    content: '● ● ● ●';
    position: absolute;
    top: 5px; left: 10px; right: 10px;
    display: flex;
    justify-content: space-between;
    font-size: 7px;
    color: rgba(255,255,255,0.25);
    letter-spacing: 220px;
    pointer-events: none;
    overflow: hidden;
  }

  /* the green board surface */
  .vks-board {
    background:
      radial-gradient(ellipse at 30% 20%, #1f4a36 0%, transparent 60%),
      radial-gradient(ellipse at 70% 80%, #1a3a2a 0%, transparent 60%),
      #1c3d2b;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    display: flex;
    flex-direction: column;
  }

  /* chalk texture */
  .vks-board::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 1;
  }

  /* faint ruled lines like a real board */
  .vks-board::after {
    content: '';
    position: absolute; inset: 0;
    background-image: repeating-linear-gradient(
      transparent,
      transparent 38px,
      rgba(255,255,255,0.025) 38px,
      rgba(255,255,255,0.025) 39px
    );
    pointer-events: none;
    z-index: 1;
  }

  /* ── slide background image ── */
  .vks-slide-bg {
    position: absolute; inset: 0;
    background-size: cover;
    background-position: center;
    z-index: 0;
    transition: opacity 0.4s ease;
  }
  .vks-slide-bg.has-image {
    opacity: 1;
  }
  .vks-slide-bg.no-image {
    opacity: 0;
  }

  /* ── top info bar on board ── */
  .vks-board-topbar {
    position: relative;
    z-index: 5;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 14px 6px;
    gap: 10px;
  }
  .vks-board-subject {
    font-family: 'Patrick Hand', cursive;
    font-size: 11px;
    color: rgba(168,216,168,0.7);
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .vks-slide-counter {
    font-family: 'Patrick Hand', cursive;
    font-size: 12px;
    color: rgba(255,255,255,0.45);
    letter-spacing: 0.5px;
  }

  /* ── chalk dots decoration (top-right) ── */
  .vks-chalk-dots {
    position: absolute;
    top: 10px;
    right: 14px;
    display: flex;
    gap: 4px;
    z-index: 5;
  }
  .vks-chalk-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    opacity: 0.4;
  }

  /* ── chapter label on board top-left ── */
  .vks-board-chapter {
    font-family: 'Patrick Hand', cursive;
    font-size: clamp(16px, 3.5vw, 24px);
    color: rgba(249,224,128,0.92);
    letter-spacing: 1.5px;
    text-transform: uppercase;
    text-shadow: 0 0 12px rgba(249,224,128,0.4), 1px 1px 0 rgba(0,0,0,0.6);
    flex: 1;
  }

  /* ── date-time on board top-right ── */
  .vks-board-datetime {
    font-family: 'Patrick Hand', cursive;
    font-size: clamp(11px, 2vw, 14px);
    color: rgba(168,216,168,0.85);
    letter-spacing: 0.5px;
    text-align: right;
    line-height: 1.4;
    text-shadow: 1px 1px 0 rgba(0,0,0,0.5);
    flex-shrink: 0;
  }

  /* ── CONTENT OVERLAY — full board, image center, QA below ── */
  .vks-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0;
  }

  /* center image area */
  .vks-center-img {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px 4px;
    min-height: 0;
  }
  .vks-center-img img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    border-radius: 6px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.5);
  }

  /* QA panel at bottom */
  .vks-qa-panel {
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0,0,0,0.55) 10%,
      rgba(0,0,0,0.88) 60%,
      rgba(0,0,0,0.95) 100%
    );
    padding: 14px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* question text — chalk style */
  .vks-q {
    font-family: 'Patrick Hand', cursive;
    font-size: clamp(16px, 3.8vw, 22px);
    color: var(--chalk-yellow);
    line-height: 1.45;
    text-shadow: 0 0 10px rgba(249,224,128,0.35), 1px 1px 0 rgba(0,0,0,0.6);
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  .vks-q-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(249,224,128,0.18);
    border: 1px solid rgba(249,224,128,0.35);
    color: var(--chalk-yellow);
    font-size: 11px;
    font-weight: 800;
    padding: 2px 7px;
    border-radius: 5px;
    font-family: 'Baloo 2', sans-serif;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    margin-top: 3px;
  }

  /* answer text — chalk style */
  .vks-a {
    font-family: 'Patrick Hand', cursive;
    font-size: clamp(14px, 3.2vw, 19px);
    color: var(--chalk-white);
    line-height: 1.55;
    text-shadow: 1px 1px 0 rgba(0,0,0,0.5);
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding-left: 2px;
  }
  .vks-a-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(232,228,216,0.12);
    border: 1px solid rgba(232,228,216,0.25);
    color: rgba(232,228,216,0.85);
    font-size: 11px;
    font-weight: 800;
    padding: 2px 7px;
    border-radius: 5px;
    font-family: 'Baloo 2', sans-serif;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    margin-top: 3px;
  }
  .vks-q-text { flex: 1; }
  .vks-a-text { flex: 1; }

  /* ── Speak-sync highlight states ── */
  .vks-q.speaking-active {
    color: #ffe033;
    text-shadow: 0 0 18px rgba(255,220,50,0.7), 0 0 6px rgba(255,220,50,0.4), 1px 1px 0 rgba(0,0,0,0.6);
    transition: color 0.25s, text-shadow 0.25s;
  }
  .vks-q.speaking-active .vks-q-badge {
    background: rgba(255,220,50,0.28);
    border-color: rgba(255,220,50,0.65);
    color: #ffe033;
    box-shadow: 0 0 10px rgba(255,220,50,0.35);
  }
  .vks-a.speaking-active {
    color: #7ee8fa;
    text-shadow: 0 0 18px rgba(126,232,250,0.65), 0 0 6px rgba(126,232,250,0.35), 1px 1px 0 rgba(0,0,0,0.6);
    transition: color 0.25s, text-shadow 0.25s;
  }
  .vks-a.speaking-active .vks-a-badge {
    background: rgba(126,232,250,0.22);
    border-color: rgba(126,232,250,0.55);
    color: #7ee8fa;
    box-shadow: 0 0 10px rgba(126,232,250,0.3);
  }
  /* dimmed state when the other line is speaking */
  .vks-q.speaking-dim,
  .vks-a.speaking-dim {
    opacity: 0.38;
    transition: opacity 0.25s;
  }

  /* Tamil font override */
  .vks-tamil {
    font-family: 'Noto Sans Tamil', sans-serif !important;
    line-height: 1.7 !important;
    font-synthesis: none !important;
    text-rendering: optimizeLegibility !important;
    -webkit-font-smoothing: antialiased !important;
  }
  .vks-tamil.vks-q { font-size: clamp(15px, 3.5vw, 20px) !important; }
  .vks-tamil.vks-a { font-size: clamp(13px, 3vw, 17px) !important; }
  /* Also fix the inner text spans for Tamil */
  .vks-tamil .vks-q-text,
  .vks-tamil .vks-a-text {
    font-family: 'Noto Sans Tamil', sans-serif !important;
  }

  /* ── chalk tray (bottom of frame) ── */
  .vks-chalk-tray {
    height: 10px;
    background: linear-gradient(180deg, #3a200a 0%, #2a1506 100%);
    border-radius: 0 0 4px 4px;
    margin-top: 2px;
    position: relative;
    overflow: hidden;
  }
  /* chalk dust on tray */
  .vks-chalk-tray::after {
    content: '';
    position: absolute;
    bottom: 0; left: 5%; right: 5%; height: 4px;
    background: rgba(232,228,216,0.25);
    border-radius: 2px;
    filter: blur(1px);
  }

  /* ── Navigation controls ── */
  .vks-nav {
    width: 100%;
    max-width: 720px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 16px;
  }
  .vks-nav-btn {
    width: 44px; height: 44px;
    border-radius: 50%;
    border: 2px solid rgba(0,0,0,0.12);
    background: #ffffff;
    color: #334155;
    font-size: 18px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .vks-nav-btn:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: rgba(0,0,0,0.22);
    transform: scale(1.1);
    box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  }
  .vks-nav-btn:disabled { opacity: 0.25; cursor: default; }

  .vks-slide-dots {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .vks-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: rgba(0,0,0,0.15);
    cursor: pointer;
    transition: all 0.2s;
  }
  .vks-dot.active {
    background: #4ade80;
    width: 20px;
    border-radius: 4px;
    box-shadow: 0 0 8px rgba(74,222,128,0.5);
  }
  .vks-dot:hover:not(.active) { background: rgba(0,0,0,0.30); }

  /* ── Bottom action bar (Read / Speak) ── */
  .vks-action-bar {
    width: 100%;
    max-width: 720px;
    display: flex;
    gap: 8px;
    margin-top: 14px;
    flex-wrap: wrap;
  }
  .vks-action-btn {
    flex: 1; min-width: 100px;
    border: none;
    border-radius: 50px;
    padding: 10px 18px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
    font-family: 'Baloo 2', sans-serif;
    letter-spacing: 0.3px;
  }
  .vks-btn-speak {
    background: rgba(74,222,128,0.10);
    border: 1.5px solid rgba(74,222,128,0.35);
    color: #16a34a;
  }
  .vks-btn-speak:hover {
    background: rgba(74,222,128,0.18);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(74,222,128,0.18);
  }
  .vks-btn-stop {
    background: rgba(248,113,113,0.08);
    border: 1.5px solid rgba(248,113,113,0.30);
    color: #dc2626;
  }
  .vks-btn-stop:hover {
    background: rgba(248,113,113,0.15);
    transform: translateY(-1px);
  }
  .vks-btn-auto {
    background: rgba(234,179,8,0.08);
    border: 1.5px solid rgba(234,179,8,0.30);
    color: #b45309;
  }
  .vks-btn-auto.running {
    background: rgba(234,179,8,0.15);
    box-shadow: 0 0 0 3px rgba(234,179,8,0.15);
    animation: vks-pulse 1.5s ease-in-out infinite;
  }
  @keyframes vks-pulse {
    0%,100% { box-shadow: 0 0 0 3px rgba(234,179,8,0.15); }
    50%      { box-shadow: 0 0 0 6px rgba(234,179,8,0.05); }
  }
  .vks-btn-auto:hover {
    background: rgba(234,179,8,0.15);
    transform: translateY(-1px);
  }

  /* ── Speaking wave ── */
  .vks-wave {
    display: inline-flex;
    align-items: flex-end;
    gap: 2px;
    height: 14px;
    vertical-align: middle;
  }
  .vks-wave span {
    display: inline-block;
    width: 3px;
    border-radius: 2px;
    background: #a8d8a8;
    animation: vks-wave-bar 0.9s ease-in-out infinite;
  }
  .vks-wave span:nth-child(1) { height: 5px;  animation-delay: 0s; }
  .vks-wave span:nth-child(2) { height: 12px; animation-delay: 0.15s; }
  .vks-wave span:nth-child(3) { height: 8px;  animation-delay: 0.3s; }
  .vks-wave span:nth-child(4) { height: 14px; animation-delay: 0.45s; }
  .vks-wave span:nth-child(5) { height: 6px;  animation-delay: 0.6s; }
  @keyframes vks-wave-bar {
    0%,100% { transform: scaleY(0.35); opacity: 0.5; }
    50%      { transform: scaleY(1);    opacity: 1;   }
  }

  /* ── Q&A fade-in for picture slides ── */
  .vks-qa-panel {
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  .vks-qa-panel.qa-hidden {
    opacity: 0;
    transform: translateY(10px);
    pointer-events: none;
  }
  .vks-qa-panel.qa-visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── Full-board mode: Q&A fills entire board ── */
  .vks-overlay.full-board {
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
  }
  .vks-overlay.full-board .vks-center-img {
    display: none;
  }
  .vks-overlay.full-board .vks-qa-panel {
    background: none;
    padding: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    padding: 20px 24px;
  }
  .vks-overlay.full-board .vks-q {
    font-size: clamp(20px, 4.5vw, 30px);
    line-height: 1.5;
  }
  .vks-overlay.full-board .vks-a {
    font-size: clamp(17px, 3.8vw, 24px);
    line-height: 1.6;
  }
  .vks-overlay.full-board .vks-tamil.vks-q { font-size: clamp(18px, 4vw, 26px) !important; }
  .vks-overlay.full-board .vks-tamil.vks-a { font-size: clamp(15px, 3.4vw, 21px) !important; }

  /* ── Image-slide progress timer bar ── */
  .vks-qa-timer-bar {
    position: absolute;
    bottom: 0; left: 0;
    height: 3px;
    background: linear-gradient(90deg, #f9e080, #7ac8f8);
    border-radius: 0 2px 2px 0;
    z-index: 20;
    box-shadow: 0 0 8px rgba(249,224,128,0.6);
    /* width is driven inline from 0% → 100% over 1.5s */
    transition: width 1.5s linear;
  }
  .vks-qa-timer-bar.done { display: none; }

  /* subtle grab cursor on board to hint swipe support */
  .vks-board { cursor: grab; }
  .vks-board:active { cursor: grabbing; }

  /* ── Slide transition animation ── */
  .vks-slide-enter {
    animation: vks-slide-in 0.35s cubic-bezier(.4,0,.2,1);
  }
  @keyframes vks-slide-in {
    from { opacity: 0; transform: translateX(28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .vks-slide-enter-left {
    animation: vks-slide-in-left 0.35s cubic-bezier(.4,0,.2,1);
  }
  @keyframes vks-slide-in-left {
    from { opacity: 0; transform: translateX(-28px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  /* ── Teacher info strip ── */
  .vks-teacher-strip {
    width: 100%;
    max-width: 720px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.08);
    border-radius: 14px;
    margin-bottom: 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .vks-teacher-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(74,222,128,0.35);
    background: #f0fdf4;
    flex-shrink: 0;
  }
  .vks-teacher-info { flex: 1; overflow: hidden; }
  .vks-teacher-name {
    font-size: 13px; font-weight: 800;
    color: #1e293b;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .vks-teacher-sub {
    font-size: 11px; color: #64748b; margin-top: 1px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .vks-updated-badge {
    font-size: 10px; font-weight: 700;
    color: #4ade80;
    background: rgba(74,222,128,0.1);
    border: 1px solid rgba(74,222,128,0.2);
    border-radius: 50px;
    padding: 2px 9px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ── Progress bar ── */
  .vks-progress-wrap {
    width: 100%;
    max-width: 720px;
    height: 3px;
    background: rgba(0,0,0,0.08);
    border-radius: 99px;
    margin-bottom: 16px;
    overflow: hidden;
  }
  .vks-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #4ade80, #a8d8a8);
    border-radius: 99px;
    transition: width 0.4s ease;
  }

  /* ── No-data ── */
  .vks-empty {
    color: #64748b;
    font-size: 15px;
    text-align: center;
    padding: 64px 16px;
    font-family: 'Baloo 2', sans-serif;
  }

  /* ── Zoom button on center image ── */
  .vks-zoom-btn {
    position: absolute;
    top: 8px; right: 8px;
    width: 32px; height: 32px;
    border-radius: 50%;
    background: rgba(0,0,0,0.55);
    border: 1.5px solid rgba(255,255,255,0.22);
    color: #e2e8f0;
    font-size: 15px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    z-index: 15;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
    backdrop-filter: blur(6px);
    line-height: 1;
  }
  .vks-zoom-btn:hover {
    background: rgba(249,224,128,0.22);
    border-color: rgba(249,224,128,0.55);
    color: #f9e080;
    transform: scale(1.12);
    box-shadow: 0 0 12px rgba(249,224,128,0.3);
  }

  /* ── Lightbox overlay ── */
  .vks-lightbox {
    position: fixed; inset: 0;
    z-index: 9999;
    background: rgba(0,0,0,0.92);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: vks-lb-in 0.22s ease;
  }
  @keyframes vks-lb-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .vks-lightbox-img {
    max-width: 92vw;
    max-height: 88vh;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 8px 60px rgba(0,0,0,0.8);
    animation: vks-lb-zoom 0.22s cubic-bezier(.34,1.56,.64,1);
  }
  @keyframes vks-lb-zoom {
    from { transform: scale(0.88); opacity: 0; }
    to   { transform: scale(1);    opacity: 1; }
  }
  .vks-lightbox-close {
    position: fixed;
    top: 18px; right: 20px;
    width: 40px; height: 40px;
    border-radius: 50%;
    background: rgba(255,255,255,0.10);
    border: 1.5px solid rgba(255,255,255,0.22);
    color: #e2e8f0;
    font-size: 20px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s;
    z-index: 10000;
  }
  .vks-lightbox-close:hover {
    background: rgba(248,113,113,0.25);
    border-color: rgba(248,113,113,0.5);
    color: #f87171;
    transform: scale(1.1);
  }
  .vks-lightbox-hint {
    position: fixed;
    bottom: 20px; left: 0; right: 0;
    text-align: center;
    font-family: 'Baloo 2', sans-serif;
    font-size: 12px;
    color: rgba(255,255,255,0.3);
    pointer-events: none;
  }

  /* ── Contact Modal ── */
  .vks-modal-backdrop {
    position: fixed; inset: 0;
    z-index: 8000;
    background: rgba(0,0,0,0.75);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: vks-lb-in 0.2s ease;
    backdrop-filter: blur(4px);
  }
  .vks-modal {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.10);
    border-radius: 18px;
    padding: 28px 28px 24px;
    width: 100%; max-width: 420px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.18);
    animation: vks-lb-zoom 0.22s cubic-bezier(.34,1.56,.64,1);
    display: flex; flex-direction: column; gap: 16px;
  }
  .vks-modal-header {
    display: flex; align-items: center; justify-content: space-between;
  }
  .vks-modal-title {
    font-family: 'Baloo 2', sans-serif;
    font-size: 17px; font-weight: 800;
    color: #1e293b;
  }
  .vks-modal-close {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: rgba(0,0,0,0.05);
    border: 1px solid rgba(0,0,0,0.10);
    color: #64748b;
    font-size: 16px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s;
  }
  .vks-modal-close:hover { background: rgba(248,113,113,0.10); color: #dc2626; }
  .vks-modal-label {
    font-family: 'Baloo 2', sans-serif;
    font-size: 11px; font-weight: 800;
    color: #64748b; letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 5px;
  }
  .vks-modal-input,
  .vks-modal-textarea {
    width: 100%;
    background: #f8fafc;
    border: 1.5px solid rgba(0,0,0,0.12);
    border-radius: 10px;
    padding: 10px 13px;
    color: #1e293b;
    font-family: 'Baloo 2', sans-serif;
    font-size: 13px;
    outline: none;
    transition: border-color 0.18s;
    resize: none;
  }
  .vks-modal-input:focus,
  .vks-modal-textarea:focus {
    border-color: rgba(74,222,128,0.55);
    background: #f0fdf4;
  }
  .vks-modal-textarea { min-height: 100px; }
  .vks-modal-send {
    width: 100%;
    background: rgba(74,222,128,0.12);
    border: 1.5px solid rgba(74,222,128,0.35);
    border-radius: 50px;
    padding: 11px;
    color: #16a34a;
    font-family: 'Baloo 2', sans-serif;
    font-size: 13px; font-weight: 800;
    cursor: pointer;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .vks-modal-send:hover:not(:disabled) {
    background: rgba(74,222,128,0.20);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(74,222,128,0.15);
  }
  .vks-modal-send:disabled { opacity: 0.5; cursor: default; }
  .vks-modal-feedback {
    text-align: center;
    font-family: 'Baloo 2', sans-serif;
    font-size: 13px; font-weight: 700;
    padding: 8px 12px;
    border-radius: 10px;
  }
  .vks-modal-feedback.success {
    background: rgba(74,222,128,0.10);
    border: 1px solid rgba(74,222,128,0.25);
    color: #16a34a;
  }
  .vks-modal-feedback.error {
    background: rgba(248,113,113,0.10);
    border: 1px solid rgba(248,113,113,0.25);
    color: #dc2626;
  }

  /* ── Share floating panel ── */
  .vks-share-panel {
    position: fixed;
    right: 18px;
    bottom: 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-radius: 18px;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(12px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    z-index: 7000;
    border: 1px solid rgba(0,0,0,0.08);
  }
  .vks-share-label {
    font-family: 'Baloo 2', sans-serif;
    font-size: 9px;
    font-weight: 800;
    color: rgba(0,0,0,0.35);
    text-align: center;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    padding: 0 2px 2px;
  }
  /* round share button */
  .vks-share-btn {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.18s cubic-bezier(.34,1.56,.64,1);
    text-decoration: none;
    flex-shrink: 0;
  }
  .vks-share-btn:hover {
    transform: translateY(-3px) scale(1.1);
    box-shadow: 0 8px 22px rgba(0,0,0,0.25);
  }
  .vks-share-wa { background: #25D366; box-shadow: 0 4px 14px rgba(37,211,102,0.35); }
  .vks-share-wa:hover { background: #1ebe5d; box-shadow: 0 8px 22px rgba(37,211,102,0.5); }
  .vks-share-fb { background: #1877F2; box-shadow: 0 4px 14px rgba(24,119,242,0.35); }
  .vks-share-fb:hover { background: #1160c9; box-shadow: 0 8px 22px rgba(24,119,242,0.5); }
  .vks-share-ig {
    background: radial-gradient(circle at 30% 110%, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    box-shadow: 0 4px 14px rgba(220,39,67,0.35);
  }
  .vks-share-ig:hover { filter: brightness(1.12); box-shadow: 0 8px 22px rgba(220,39,67,0.55); }

  /* ── Bookmark button ── */
  .vks-btn-bookmark {
    background: rgba(234,179,8,0.08);
    border: 1.5px solid rgba(234,179,8,0.30);
    color: #b45309;
  }
  .vks-btn-bookmark.bookmarked {
    background: rgba(234,179,8,0.18);
    border-color: rgba(234,179,8,0.55);
    color: #92400e;
    box-shadow: 0 0 0 3px rgba(234,179,8,0.12);
  }
  .vks-btn-bookmark:hover { background: rgba(234,179,8,0.15); transform: translateY(-1px); }

  /* ── Export PDF button ── */
  .vks-btn-pdf {
    background: rgba(248,113,113,0.08);
    border: 1.5px solid rgba(248,113,113,0.28);
    color: #dc2626;
  }
  .vks-btn-pdf:hover { background: rgba(248,113,113,0.15); transform: translateY(-1px); }
  .vks-btn-pdf:disabled { opacity: 0.5; cursor: default; }

  /* ── Bookmark drawer ── */
  .vks-bookmark-modal {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.10);
    border-radius: 18px;
    padding: 24px 24px 20px;
    width: 100%; max-width: 480px;
    max-height: 75vh;
    overflow-y: auto;
    box-shadow: 0 24px 80px rgba(0,0,0,0.15);
    animation: vks-lb-zoom 0.22s cubic-bezier(.34,1.56,.64,1);
    display: flex; flex-direction: column; gap: 14px;
  }
  .vks-bookmark-modal::-webkit-scrollbar { width: 4px; }
  .vks-bookmark-modal::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 99px; }
  .vks-bookmark-item {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px solid rgba(0,0,0,0.07);
    border-radius: 12px; cursor: pointer;
    transition: all 0.18s;
  }
  .vks-bookmark-item:hover { background: #fefce8; border-color: rgba(234,179,8,0.30); }
  .vks-bookmark-num {
    font-family: 'Baloo 2', sans-serif; font-size: 12px; color: #b45309;
    background: rgba(234,179,8,0.10); border: 1px solid rgba(234,179,8,0.25);
    border-radius: 6px; padding: 2px 8px; flex-shrink: 0; margin-top: 2px;
  }
  .vks-bookmark-content { flex: 1; min-width: 0; }
  .vks-bookmark-q {
    font-family: 'Baloo 2', sans-serif; font-size: 13px; font-weight: 700;
    color: #1e293b; line-height: 1.4;
    overflow: hidden; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }
  .vks-bookmark-meta { font-size: 11px; color: #64748b; margin-top: 3px; }
  .vks-bookmark-del {
    background: none; border: none; color: #94a3b8;
    font-size: 16px; cursor: pointer; flex-shrink: 0;
    transition: color 0.15s; padding: 2px 4px; line-height: 1;
  }
  .vks-bookmark-del:hover { color: #dc2626; }
  .vks-bookmark-empty {
    text-align: center; color: #64748b;
    font-family: 'Baloo 2', sans-serif; font-size: 14px; padding: 24px 0;
  }

  /* ── PDF export progress overlay ── */
  .vks-pdf-overlay {
    position: fixed; inset: 0; z-index: 9000;
    background: rgba(0,0,0,0.82);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 18px;
    animation: vks-lb-in 0.2s ease; backdrop-filter: blur(6px);
  }
  .vks-pdf-label {
    font-family: 'Baloo 2', sans-serif;
    font-size: 15px; font-weight: 800; color: #f9e080;
  }
  .vks-pdf-progress-wrap {
    width: 280px; height: 6px;
    background: rgba(255,255,255,0.10); border-radius: 99px; overflow: hidden;
  }
  .vks-pdf-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #f9e080, #f4a7b9);
    border-radius: 99px; transition: width 0.3s ease;
  }
  .vks-pdf-sub { font-family: 'Baloo 2', sans-serif; font-size: 12px; color: #64748b; }

  /* ── Mobile ── */
  @media (max-width: 480px) {
    .vks-q  { font-size: 15px; }
    .vks-a  { font-size: 13px; }
    .vks-qa-panel { padding: 10px 12px 12px; gap: 6px; }
    .vks-action-btn { font-size: 11px; padding: 9px 12px; }
    .vks-modal { padding: 22px 18px 18px; }
    .vks-share-panel { right: 10px; bottom: 10px; flex-direction: row; gap: 6px; padding: 8px; border-radius: 999px; }
    .vks-share-label { display: none; }
    .vks-share-btn { width: 36px; height: 36px; }
  }
`;

// ─────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────
const BASE = "/resources/";
const DEFAULT_BOARD = null; // null = use pure board green; set a path to use an image

function groupByTopic(rows) {
  // Group rows by question_id (same topic = multiple slides)
  const map = {};
  rows.forEach((r) => {
    const id = r.question_id ?? r.question_eng; // fallback if no question_id
    if (!map[id]) map[id] = [];
    map[id].push(r);
  });
  // Sort each group by question_id_seq
  Object.values(map).forEach((group) =>
    group.sort((a, b) => (a.question_id_seq ?? 0) - (b.question_id_seq ?? 0))
  );
  return Object.values(map); // array of groups
}

// Returns:
//   null          → default_board / empty → full-board Q&A mode
//   string (url)  → real picture → show image first, then Q&A after delay
function slideImage(row, lang) {
  const img = lang === "ta" ? row.slide_tamil : row.slide;
  if (!img || img === "default_board.png" || img === "") return null;
  return BASE + img;
}

// Is the current slide in "full board" (no picture) mode?
function isFullBoardMode(row, lang) {
  if (!row) return true;
  const img = lang === "ta" ? row.slide_tamil : row.slide;
  return !img || img === "default_board.png" || img === "";
}

// ─────────────────────────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────────────────────────
export default function VKTemplateSlide({ rows, onHome, onContact, brandName = "VK Slides" }) {
  const [lang, setLang]             = useState("en"); // "en" | "ta"
  const [topicIdx, setTopicIdx]     = useState(0);
  const [slideIdx, setSlideIdx]     = useState(0);
  const [animDir, setAnimDir]       = useState("right"); // for enter animation
  const [animKey, setAnimKey]       = useState(0);
  const [speaking, setSpeaking]     = useState(false);
  const [speakPhase, setSpeakPhase] = useState(null); // null | "q" | "a"
  const [autoPlay, setAutoPlay]     = useState(false);
  const [now, setNow]               = useState(new Date());
  const [qaVisible, setQaVisible]   = useState(true);
  const [timerWidth, setTimerWidth] = useState(0);
  // ── Zoom lightbox ─────────────────────────────────────
  const [zoomOpen, setZoomOpen]     = useState(false);
  // ── Contact modal ─────────────────────────────────────
  const [contactOpen, setContactOpen]   = useState(false);
  const [contactSubject, setContactSubject] = useState("");
  const [contactMsg, setContactMsg]         = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactFeedback, setContactFeedback] = useState(null); // {type:"success"|"error", text}
  const autoRef    = useRef(null);
  const cancelRef  = useRef(false);
  const qaTimerRef = useRef(null);
  const boardRef    = useRef(null);   // attached to .vks-board for swipe detection
  const frameRef    = useRef(null);   // attached to .vks-board-frame for screenshot capture
  const touchStartX = useRef(null);
  const [capturing, setCapturing] = useState(false);
  // ── Bookmark state ─────────────────────────────────────
  const [bookmarks, setBookmarks]       = useState(() => {
    try { return JSON.parse(localStorage.getItem("vks_bookmarks") || "[]"); } catch { return []; }
  });
  const [bookmarkOpen, setBookmarkOpen] = useState(false);
  // ── PDF export state ───────────────────────────────────
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress,  setPdfProgress]  = useState(0);
  const [pdfLabel,     setPdfLabel]     = useState("");

  // live clock — tick every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Keyboard navigation ────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      // Close lightbox or modal first
      if (e.key === "Escape") {
        if (zoomOpen)    { setZoomOpen(false);    return; }
        if (contactOpen) { setContactOpen(false); return; }
        stopSpeech();
        return;
      }
      // Don't navigate when modal/lightbox is open or focus is on input
      if (zoomOpen || contactOpen) return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown")  { e.preventDefault(); nextSlide(); }
      if (e.key === "ArrowLeft"  || e.key === "ArrowUp")    { e.preventDefault(); prevSlide(); }
      if (e.key === " ")                                     { e.preventDefault(); speakSlide(currentSlide, lang); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // no dep array — always fresh closures

  // ── Contact form submit ────────────────────────────────────
  const handleSendContact = async () => {
    if (!contactSubject.trim() || !contactMsg.trim()) {
      setContactFeedback({ type: "error", text: "Please fill in both fields." });
      return;
    }
    setContactSending(true);
    setContactFeedback(null);
    const result = await sendContactMail(contactSubject.trim(), contactMsg.trim());
    setContactSending(false);
    if (result.success) {
      setContactFeedback({ type: "success", text: "✅ " + result.message });
      setContactSubject("");
      setContactMsg("");
      setTimeout(() => { setContactOpen(false); setContactFeedback(null); }, 2200);
    } else {
      setContactFeedback({ type: "error", text: "❌ " + result.message });
    }
  };

  // ── Capture board as image, download it, then open share URL ──
  const captureAndShare = async (platform) => {
    if (!frameRef.current || capturing) return;
    setCapturing(true);
    try {
      // Dynamically load html2canvas from CDN if not already loaded
      if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      // Render the board frame to canvas
      const canvas = await window.html2canvas(frameRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 2,          // retina quality
        logging: false,
      });

      // Build a filename from chapter + slide index
      const chapter = (currentSlide?.chapter || "slide").replace(/\s+/g, "_");
      const filename = `${chapter}_slide${slideIdx + 1}.png`;

      // Trigger download
      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();

      // Small pause so download dialog appears before the new tab opens
      await new Promise((r) => setTimeout(r, 600));

      // Open the share URL
      const pageUrl = encodeURIComponent(window.location.href);
      if (platform === "facebook") {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`,
          "_blank", "noopener,noreferrer"
        );
      } else if (platform === "instagram") {
        // Instagram web has no file-attach API; link is already copied to clipboard
        try { navigator.clipboard.writeText(window.location.href); } catch (_) {}
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Capture error:", err);
    } finally {
      setCapturing(false);
    }
  };

  // ── Bookmark helpers ─────────────────────────────────────
  const bookmarkKey = (tIdx, sIdx) => `${tIdx}:${sIdx}`;

  const isBookmarked = (tIdx, sIdx) =>
    bookmarks.some((b) => b.key === bookmarkKey(tIdx, sIdx));

  const toggleBookmark = () => {
    const key  = bookmarkKey(topicIdx, slideIdx);
    const slide = currentSlide;
    if (!slide) return;
    let next;
    if (isBookmarked(topicIdx, slideIdx)) {
      next = bookmarks.filter((b) => b.key !== key);
    } else {
      next = [...bookmarks, {
        key,
        topicIdx,
        slideIdx,
        question: slide.question_eng,
        chapter:  slide.chapter || "",
        subject:  meta.subject_name || "",
      }];
    }
    setBookmarks(next);
    try { localStorage.setItem("vks_bookmarks", JSON.stringify(next)); } catch {}
  };

  const removeBookmark = (key) => {
    const next = bookmarks.filter((b) => b.key !== key);
    setBookmarks(next);
    try { localStorage.setItem("vks_bookmarks", JSON.stringify(next)); } catch {}
  };

  const jumpToBookmark = (b) => {
    setTopicIdx(b.topicIdx);
    setSlideIdx(b.slideIdx);
    setAnimDir("right");
    setAnimKey((k) => k + 1);
    stopSpeech();
    setBookmarkOpen(false);
  };

  // ── PDF export — bookmarked Q&A only, with embedded Tamil font ──────────
  const exportPDF = async () => {
    if (pdfExporting) return;

    if (bookmarks.length === 0) {
      alert("No bookmarks found.\nBookmark some slides first using the 🔖 Bookmark button.");
      return;
    }

    setPdfExporting(true);
    setPdfProgress(0);
    setPdfLabel("Loading PDF engine…");

    try {
      // ── 1. Load jsPDF ──────────────────────────────────────
      if (!window.jspdf) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }

      // ── 2. Fetch & embed NotoSansTamil font as Base64 ──────
      //    We fetch the TTF from Google Fonts static CDN,
      //    convert to base64, then register with jsPDF so Tamil
      //    characters render properly instead of showing boxes.
      setPdfLabel("Downloading Tamil font…");

      let tamilFontB64 = window._vksTamilFontB64 || null;

      if (!tamilFontB64) {
        try {
          // jsPDF requires a TTF (not woff/woff2).
          // Try multiple CDN sources for Noto Sans Tamil TTF in order.
          const ttfUrls = [
            // fontsource via jsDelivr — reliable TTF
            "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans-tamil/files/noto-sans-tamil-tamil-400-normal.woff2",
            // Direct Google Fonts static TTF (non-subset, works with jsPDF)
            "https://fonts.gstatic.com/s/notosanstamil/v32/ieVc2YdFI3GCY6SyQy1KfStzYKZgzN1z4LKDbeZce-0429tBManUktuex7vGo70RqKDt.ttf",
            // Another Google Fonts static path variant
            "https://fonts.gstatic.com/s/notosanstamil/v32/ieVc2YdFI3GCY6SyQy1KfStzYKZgzN1z4LKDbeZce-04.ttf",
          ];

          let buf = null;
          for (const url of ttfUrls) {
            try {
              const resp = await fetch(url);
              if (resp.ok) { buf = await resp.arrayBuffer(); break; }
            } catch (_) { /* try next */ }
          }

          // Last resort: ask Google Fonts CSS API with a TTF-returning UA
          if (!buf) {
            const cssResp = await fetch(
              "https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400",
              { headers: { "User-Agent": "Mozilla/4.0 (compatible; MSIE 6.0)" } }
            );
            if (cssResp.ok) {
              const css = await cssResp.text();
              const match = css.match(/url\((https:\/\/[^)]+\.ttf[^)]*)\)/);
              if (match) {
                const r2 = await fetch(match[1]);
                if (r2.ok) buf = await r2.arrayBuffer();
              }
            }
          }

          if (!buf) throw new Error("All Tamil font fetch attempts failed");

          // Convert ArrayBuffer → base64 in safe chunks (avoids stack overflow)
          const bytes = new Uint8Array(buf);
          let binary = "";
          const CHUNK = 8192;
          for (let b = 0; b < bytes.byteLength; b += CHUNK) {
            binary += String.fromCharCode(...bytes.subarray(b, b + CHUNK));
          }
          tamilFontB64 = btoa(binary);
          window._vksTamilFontB64 = tamilFontB64;
        } catch (fontErr) {
          console.warn("Tamil font fetch failed, will skip Tamil in PDF:", fontErr);
          tamilFontB64 = null;
        }
      }

      // ── 3. Build the PDF ───────────────────────────────────
      setPdfLabel("Building PDF…");

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      // Register the Tamil font with jsPDF (must be TTF data)
      const TAMIL_FONT = "NotoSansTamil";
      if (tamilFontB64) {
        pdf.addFileToVFS("NotoSansTamil-Regular.ttf", tamilFontB64);
        pdf.addFont("NotoSansTamil-Regular.ttf", TAMIL_FONT, "normal");
        pdf.addFont("NotoSansTamil-Regular.ttf", TAMIL_FONT, "bold");
      }

      // Helper — switch font safely
      const setTamilFont  = () => tamilFontB64
        ? pdf.setFont(TAMIL_FONT, "normal")
        : pdf.setFont("helvetica", "normal");
      const setLatinFont  = (style = "normal") => pdf.setFont("helvetica", style);

      /**
       * renderTamilAsImage — renders Tamil text via a hidden <canvas> using the
       * browser's own text-shaping engine (handles Tamil ligatures/conjuncts
       * correctly), then embeds the result as a PNG image in the PDF.
       * This is required because jsPDF does NOT perform Unicode complex-script
       * shaping internally — Tamil rendered via pdf.text() comes out garbled.
       */
      const renderTamilAsImage = (text, xMm, yMm, wMm, opts = {}) => {
        if (!text || !text.trim()) return 0;
        const { fontSize = 15, color = "#1e293b", lineHeightFactor = 1.75 } = opts;
        const DPR    = 3;          // render at 3× for crispness
        const MM2PX  = 3.7795;
        const canvasW = Math.round(wMm * MM2PX * DPR);
        const fontPx  = Math.round(fontSize * DPR);

        // Measure + wrap
        const measureCanvas = document.createElement("canvas");
        const mctx = measureCanvas.getContext("2d");
        mctx.font = `${fontPx}px "Noto Sans Tamil", sans-serif`;

        const words = text.split(/\s+/);
        const lines = [];
        let cur = "";
        for (const w of words) {
          const test = cur ? cur + " " + w : w;
          if (mctx.measureText(test).width <= canvasW - fontPx) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);

        const lhPx   = Math.round(fontPx * lineHeightFactor);
        const padPx  = Math.round(fontPx * 0.3);
        const totalH = lhPx * lines.length + padPx * 2;

        const can = document.createElement("canvas");
        can.width  = canvasW;
        can.height = totalH;
        const ctx  = can.getContext("2d");
        ctx.font   = `${fontPx}px "Noto Sans Tamil", sans-serif`;
        ctx.fillStyle    = color;
        ctx.textBaseline = "top";
        lines.forEach((line, i) => {
          ctx.fillText(line, 0, padPx + i * lhPx);
        });

        const imgData  = can.toDataURL("image/png");
        const heightMm = totalH / (MM2PX * DPR);
        pdf.addImage(imgData, "PNG", xMm, yMm, wMm, heightMm);
        return heightMm;
      };

      const PW = 210;
      const PH = 297;
      const ML = 18;
      const MR = 18;
      const TW = PW - ML - MR;
      const total = bookmarks.length;

      // ── COVER PAGE ────────────────────────────────────────
      pdf.setFillColor(15, 35, 24);
      pdf.rect(0, 0, PW, PH, "F");
      pdf.setFillColor(26, 58, 42);
      pdf.rect(0, 0, PW, 52, "F");

      // Brand
      setLatinFont("bold");
      pdf.setTextColor(168, 216, 168);
      pdf.setFontSize(22);
      pdf.text("virtualkalvi.com", PW / 2, 22, { align: "center" });

      pdf.setTextColor(249, 224, 128);
      pdf.setFontSize(12);
      setLatinFont("normal");
      pdf.text("Knowledge is Power", PW / 2, 32, { align: "center" });

      // Tamil tagline on cover - rendered as canvas image for correct shaping
      renderTamilAsImage("அறிவு தான் சமத்துவத்தின் அடித்தளம்", ML, 36, TW, { fontSize: 11, color: "#a8d8a8" });

      // Divider
      pdf.setDrawColor(168, 216, 168);
      pdf.setLineWidth(0.4);
      pdf.line(ML, 56, PW - MR, 56);

      // Title
      setLatinFont("bold");
      pdf.setTextColor(232, 228, 216);
      pdf.setFontSize(18);
      pdf.text("Bookmarked Q&A", PW / 2, 72, { align: "center" });

      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(10);
      setLatinFont("normal");
      pdf.text(
        `${total} bookmarked question${total !== 1 ? "s" : ""}`,
        PW / 2, 82, { align: "center" }
      );

      const firstB = bookmarks[0];
      if (firstB.subject || firstB.chapter) {
        pdf.setTextColor(122, 200, 248);
        pdf.setFontSize(10);
        pdf.text(
          [firstB.chapter, firstB.subject].filter(Boolean).join("  ·  "),
          PW / 2, 92, { align: "center" }
        );
      }

      pdf.setTextColor(71, 85, 105);
      pdf.setFontSize(9);
      pdf.text(
        `Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
        PW / 2, PH - 14, { align: "center" }
      );

      // ── Q&A PAGES ─────────────────────────────────────────
      for (let i = 0; i < total; i++) {
        const b         = bookmarks[i];
        const slideData = (topics[b.topicIdx] ?? [])[b.slideIdx] ?? null;

        const qEn = slideData?.question_eng   || b.question || "—";
        const aEn = slideData?.answer_english || "—";
        const qTa = slideData?.question_tamil || "";
        const aTa = slideData?.answer_tamil   || "";

        setPdfLabel(`Writing Q&A ${i + 1} of ${total}…`);
        setPdfProgress(Math.round(((i + 1) / total) * 100));

        pdf.addPage();

        // Background
        pdf.setFillColor(15, 35, 24);
        pdf.rect(0, 0, PW, PH, "F");

        // Top bar
        pdf.setFillColor(26, 58, 42);
        pdf.rect(0, 0, PW, 16, "F");

        // Breadcrumb (Latin)
        setLatinFont("normal");
        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(8);
        const crumb = [b.chapter, b.subject].filter(Boolean).join(" · ");
        pdf.text(crumb || "Bookmarked Slide", ML, 10.5);

        // Slide counter
        setLatinFont("bold");
        pdf.setTextColor(249, 224, 128);
        pdf.setFontSize(8);
        pdf.text(`${i + 1} / ${total}`, PW - MR, 10.5, { align: "right" });

        let y = 26;

        // ══ ENGLISH SECTION ══════════════════════════════════
        // Section label
        setLatinFont("bold");
        pdf.setTextColor(180, 200, 180);
        pdf.setFontSize(7.5);
        pdf.text("ENGLISH", ML, y);
        y += 5;

        // Q pill + text
        pdf.setFillColor(40, 65, 35);
        pdf.roundedRect(ML, y - 4, 12, 6, 2, 2, "F");
        setLatinFont("bold");
        pdf.setTextColor(249, 224, 128);
        pdf.setFontSize(8);
        pdf.text("Q", ML + 6, y, { align: "center" });

        pdf.setTextColor(249, 224, 128);
        pdf.setFontSize(12);
        setLatinFont("bold");
        const qLines = pdf.splitTextToSize(qEn, TW - 16);
        pdf.text(qLines, ML + 16, y);
        y += qLines.length * 6.2 + 5;

        // A pill + text
        pdf.setFillColor(30, 55, 40);
        pdf.roundedRect(ML, y - 4, 12, 6, 2, 2, "F");
        setLatinFont("bold");
        pdf.setTextColor(168, 216, 168);
        pdf.setFontSize(8);
        pdf.text("A", ML + 6, y, { align: "center" });

        pdf.setTextColor(232, 228, 216);
        pdf.setFontSize(11);
        setLatinFont("normal");
        const aLines = pdf.splitTextToSize(aEn, TW - 16);
        pdf.text(aLines, ML + 16, y);
        y += aLines.length * 5.8 + 10;

        // ══ TAMIL SECTION ════════════════════════════════════
        if (qTa && qTa.trim()) {
          // Divider
          pdf.setDrawColor(45, 65, 55);
          pdf.setLineWidth(0.4);
          pdf.line(ML, y, PW - MR, y);
          y += 6;

          // Section label — rendered as image for correct Tamil shaping
          y += renderTamilAsImage("தமிழ் (Tamil)", ML, y - 5, TW, { fontSize: 9, color: "#7ac8f8" });
          y += 2;

          // Tamil Q — pill + canvas-rendered text
          pdf.setFillColor(30, 45, 65);
          pdf.roundedRect(ML, y - 4, 12, 6, 2, 2, "F");
          setLatinFont("bold");
          pdf.setTextColor(122, 200, 248);
          pdf.setFontSize(8);
          pdf.text("Q", ML + 6, y, { align: "center" });

          // Render Tamil Q text as canvas image (correct script shaping)
          const qTaH = renderTamilAsImage(qTa, ML + 16, y - 4, TW - 18, { fontSize: 13, color: "#7ac8f8" });
          y += Math.max(qTaH, 7) + 4;

          // Tamil A — pill + canvas-rendered text
          if (aTa && aTa.trim()) {
            pdf.setFillColor(25, 50, 38);
            pdf.roundedRect(ML, y - 4, 12, 6, 2, 2, "F");
            setLatinFont("bold");
            pdf.setTextColor(168, 216, 168);
            pdf.setFontSize(8);
            pdf.text("A", ML + 6, y, { align: "center" });

            // Render Tamil A text as canvas image (correct script shaping)
            renderTamilAsImage(aTa, ML + 16, y - 4, TW - 18, { fontSize: 12, color: "#c8e6c8" });
          }
        }

        // ── Bottom watermark ────────────────────────────────
        pdf.setDrawColor(26, 58, 42);
        pdf.setLineWidth(0.3);
        pdf.line(ML, PH - 14, PW - MR, PH - 14);
        setLatinFont("normal");
        pdf.setTextColor(71, 85, 105);
        pdf.setFontSize(8);
        pdf.text("virtualkalvi.com", ML, PH - 8);
        pdf.text(`Bookmark ${i + 1} of ${total}`, PW - MR, PH - 8, { align: "right" });
      }

      setPdfLabel("Saving PDF…");
      setPdfProgress(100);

      const subject = (bookmarks[0]?.subject || bookmarks[0]?.chapter || "bookmarks")
        .replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
      pdf.save(`virtualkalvi_bookmarks_${subject}.pdf`);

    } catch (err) {
      console.error("PDF export error:", err);
      alert("PDF export failed. Please try again.");
    } finally {
      setPdfExporting(false);
      setPdfProgress(0);
      setPdfLabel("");
    }
  };
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd   = (e) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return; // ignore tiny taps
      if (dx < 0) nextSlide(); else prevSlide();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }); // no dep array — always has fresh nextSlide/prevSlide closures

  // (Q&A reveal effect is placed after useMemo below, where currentSlide is available)

  // Inject CSS once
  useEffect(() => {
    const id = "vks-css";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = CSS;
      document.head.appendChild(el);
    }
  }, []);

  const topics = useMemo(() => groupByTopic(rows), [rows]);

  // Q&A reveal + progress timer bar + next-slide image preload
  useEffect(() => {
    if (qaTimerRef.current) clearTimeout(qaTimerRef.current);
    const group = topics[topicIdx] ?? [];
    const slide = group[slideIdx] ?? null;

    // ── Preload adjacent images to eliminate flicker ──────────
    [-1, 1].forEach((offset) => {
      let tIdx = topicIdx, sIdx = slideIdx + offset;
      if (sIdx < 0 && tIdx > 0) { tIdx--; sIdx = (topics[tIdx]?.length ?? 1) - 1; }
      if (sIdx >= (topics[tIdx]?.length ?? 0) && tIdx < topics.length - 1) { tIdx++; sIdx = 0; }
      const neighbor = (topics[tIdx] ?? [])[sIdx];
      if (neighbor) {
        ["en", "ta"].forEach((l) => {
          const src = slideImage(neighbor, l);
          if (src) { const img = new Image(); img.src = src; }
        });
      }
    });

    if (isFullBoardMode(slide, lang)) {
      // Full-board: show Q&A immediately, no timer bar
      setQaVisible(true);
      setTimerWidth(0);
    } else {
      // Picture slide: hide Q&A, animate timer bar 0→100% then reveal
      setQaVisible(false);
      setTimerWidth(0);
      // A tiny rAF delay lets the CSS transition see the 0→100 change
      requestAnimationFrame(() => requestAnimationFrame(() => setTimerWidth(100)));
      qaTimerRef.current = setTimeout(() => {
        setQaVisible(true);
        setTimerWidth(0); // reset so it hides (bar uses display:none when done)
      }, 1500);
    }
    return () => { if (qaTimerRef.current) clearTimeout(qaTimerRef.current); };
  }, [animKey, lang]); // animKey increments on every navigation

  if (!rows || rows.length === 0) {
    return <div className="vks-empty">📭 No slides available</div>;
  }

  const currentGroup = topics[topicIdx] ?? [];
  const currentSlide = currentGroup[slideIdx] ?? null;
  const totalSlides  = currentGroup.length;
  const totalTopics  = topics.length;

  // ── navigation ──────────────────────────────────────
  const goToSlide = (idx, dir = "right") => {
    setAnimDir(dir);
    setAnimKey((k) => k + 1);
    setSlideIdx(idx);
    stopSpeech();
  };

  const prevSlide = () => {
    if (slideIdx > 0) {
      goToSlide(slideIdx - 1, "left");
    } else if (topicIdx > 0) {
      // go to last slide of previous topic
      const prevGroup = topics[topicIdx - 1];
      setTopicIdx(topicIdx - 1);
      goToSlide(prevGroup.length - 1, "left");
    }
  };

  const nextSlide = () => {
    if (slideIdx < totalSlides - 1) {
      goToSlide(slideIdx + 1, "right");
    } else if (topicIdx < totalTopics - 1) {
      setTopicIdx(topicIdx + 1);
      goToSlide(0, "right");
    }
  };

  const selectTopic = (idx) => {
    setTopicIdx(idx);
    setSlideIdx(0);
    setAnimDir("right");
    setAnimKey((k) => k + 1);
    stopSpeech();
  };

  // global prev/next across all slides
  const isFirst = topicIdx === 0 && slideIdx === 0;
  const isLast  = topicIdx === totalTopics - 1 && slideIdx === totalSlides - 1;

  // ── speech ──────────────────────────────────────────
  const stopSpeech = () => {
    cancelRef.current = true;
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setSpeakPhase(null);
    setAutoPlay(false);
    if (autoRef.current) clearTimeout(autoRef.current);
  };

  const speakSlide = (row, language) => {
    if (!row) return;
    cancelRef.current = false;
    window.speechSynthesis.cancel();
    setSpeaking(true);
    setSpeakPhase("q");

    const voices = window.speechSynthesis.getVoices();
    const taVoice = voices.find((v) => v.lang === "ta-IN") || voices.find((v) => v.lang.startsWith("ta"));
    const enVoice = voices.find((v) => v.lang === "en-US") || voices.find((v) => v.lang.startsWith("en"));

    const phases = ["q", "a"];
    const lines = language === "ta"
      ? [`${row.question_tamil}`, `${row.answer_tamil}`]
      : [`${row.question_eng}`, `${row.answer_english}`];

    let i = 0;
    const speakNext = () => {
      if (cancelRef.current || i >= lines.length) {
        setSpeaking(false);
        setSpeakPhase(null);
        return;
      }
      setSpeakPhase(phases[i]);
      const u = new SpeechSynthesisUtterance(lines[i]);
      u.rate = 0.9;
      if (language === "ta" && taVoice) { u.voice = taVoice; u.lang = "ta-IN"; }
      else if (enVoice) { u.voice = enVoice; u.lang = "en-US"; }
      u.onend = () => { i++; speakNext(); };
      u.onerror = () => { setSpeaking(false); setSpeakPhase(null); };
      window.speechSynthesis.speak(u);
    };
    speakNext();
  };

  // ── auto-advance slideshow ───────────────────────────
  const toggleAutoPlay = () => {
    if (autoPlay) {
      stopSpeech();
    } else {
      setAutoPlay(true);
      speakAndAdvance(topicIdx, slideIdx);
    }
  };

  const speakAndAdvance = (tIdx, sIdx) => {
    const group = topics[tIdx];
    if (!group) { setAutoPlay(false); return; }
    const slide = group[sIdx];
    if (!slide) { setAutoPlay(false); return; }
    if (cancelRef.current) return;

    cancelRef.current = false;
    window.speechSynthesis.cancel();
    setSpeaking(true);
    setSpeakPhase("q");
    setTopicIdx(tIdx);
    setSlideIdx(sIdx);
    setAnimDir("right");
    setAnimKey((k) => k + 1);

    const voices = window.speechSynthesis.getVoices();
    const taVoice = voices.find((v) => v.lang === "ta-IN") || voices.find((v) => v.lang.startsWith("ta"));
    const enVoice = voices.find((v) => v.lang === "en-US") || voices.find((v) => v.lang.startsWith("en"));

    const lines = lang === "ta"
      ? [slide.question_tamil, slide.answer_tamil]
      : [slide.question_eng, slide.answer_english];

    const phases = ["q", "a"];
    let i = 0;
    const speakNext = () => {
      if (cancelRef.current) { setSpeaking(false); setSpeakPhase(null); return; }
      if (i >= lines.length) {
        setSpeaking(false);
        setSpeakPhase(null);
        // advance
        autoRef.current = setTimeout(() => {
          if (cancelRef.current) return;
          let nextSIdx = sIdx + 1;
          let nextTIdx = tIdx;
          if (nextSIdx >= (topics[nextTIdx]?.length ?? 0)) {
            nextTIdx++;
            nextSIdx = 0;
          }
          if (nextTIdx >= topics.length) {
            setAutoPlay(false);
          } else {
            speakAndAdvance(nextTIdx, nextSIdx);
          }
        }, 1200);
        return;
      }
      const u = new SpeechSynthesisUtterance(lines[i] || "");
      u.rate = 0.9;
      setSpeakPhase(phases[i]);
      if (lang === "ta" && taVoice) { u.voice = taVoice; u.lang = "ta-IN"; }
      else if (enVoice) { u.voice = enVoice; u.lang = "en-US"; }
      u.onend = () => { i++; speakNext(); };
      u.onerror = () => { setSpeaking(false); setSpeakPhase(null); };
      window.speechSynthesis.speak(u);
    };
    speakNext();
  };

  // ── image source & board mode ────────────────────────
  const imgSrc    = currentSlide ? slideImage(currentSlide, lang) : null;
  const fullBoard = isFullBoardMode(currentSlide, lang);

  // ── teacher info from first slide in current group ───
  const meta = currentGroup[0] ?? {};

  // ── progress across ALL slides globally ─────────────
  const allSlides = topics.flatMap((g) => g);
  const globalIdx = topics.slice(0, topicIdx).reduce((s, g) => s + g.length, 0) + slideIdx;
  const progressPct = allSlides.length > 1 ? (globalIdx / (allSlides.length - 1)) * 100 : 100;

  // ── Tamil vs English helpers ─────────────────────────
  const isTa = lang === "ta";
  const qText = currentSlide ? (isTa ? currentSlide.question_tamil : currentSlide.question_eng) : "";
  const aText = currentSlide ? (isTa ? currentSlide.answer_tamil   : currentSlide.answer_english) : "";

  // ── chalk dot colors ─────────────────────────────────
  const dotColors = ["#f9e080","#7ac8f8","#f4a7b9","#a8d8a8","#c4b5fd"];

  // ── live clock ────────────────────────────────────────
  const dateStr = now.toLocaleDateString("en-IN", { weekday:"short", day:"2-digit", month:"short", year:"numeric" });
  const timeStr = now.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:true });

  return (
    <>
      {/* ── TOP NAVIGATION BAR ── */}
      <nav className="vks-navbar">
        <div className="vks-navbar-brand">
          🎓 <span>{brandName}</span>
        </div>
        <div className="vks-navbar-links">
          <button
            className="vks-nav-link vks-nav-link-home"
            onClick={() => onHome ? onHome() : window.location.href = "/"}
            title="Go to Home"
          >
            🏠 Home
          </button>
          <button
            className="vks-nav-link vks-nav-link-contact"
            onClick={() => { setContactOpen(true); setContactFeedback(null); }}
            title="Contact Us"
          >
            ✉️ Contact
          </button>
        </div>
      </nav>

    <div className="vks-root">

      {/* ── TEACHER STRIP ── */}
      {meta.teacher_name && (
        <div className="vks-teacher-strip">
          <img
            className="vks-teacher-avatar"
            src={meta.teacher_picture ? BASE + meta.teacher_picture : "/default-avatar.png"}
            alt={meta.teacher_name}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="vks-teacher-info">
            <div className="vks-teacher-name">{meta.teacher_name}</div>
            <div className="vks-teacher-sub">
              {[meta.institution_name, meta.grade_name, meta.subject_name]
                .filter(Boolean).join(" · ")}
            </div>
          </div>
          {meta.updated_ago && (
            <div className="vks-updated-badge">🕐 {meta.updated_ago}</div>
          )}
        </div>
      )}

      {/* ── GLOBAL PROGRESS BAR ── */}
      <div className="vks-progress-wrap">
        <div className="vks-progress-bar" style={{ width: `${progressPct}%` }} />
      </div>

      {/* ── TOPIC PILLS ── */}
      {totalTopics > 1 && (
        <div className="vks-topic-scroll">
          {topics.map((group, i) => {
            const label = group[0]?.chapter || `Topic ${i + 1}`;
            return (
              <button
                key={i}
                className={`vks-topic-pill ${i === topicIdx ? "active" : ""}`}
                onClick={() => selectTopic(i)}
              >
                {label}
                <span style={{ marginLeft: 5, opacity: 0.6, fontSize: 10 }}>
                  ({group.length})
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="vks-header">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div className="vks-chapter-title">
            {currentSlide?.chapter || "Slides"}
          </div>
          {/* Lang toggle */}
          <div className="vks-lang-bar">
            {[["en","🇬🇧 English"],["ta","🇮🇳 தமிழ்"]].map(([k,lbl]) => (
              <button
                key={k}
                className={`vks-lang-btn ${lang === k ? "active" : ""}`}
                onClick={() => { setLang(k); stopSpeech(); }}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="vks-meta">
          {meta.grade_name   && <span className="vks-meta-pill">📚 {meta.grade_name}</span>}
          {meta.subject_name && <span className="vks-meta-pill">📖 {meta.subject_name}</span>}
          {meta.affiliation  && <span className="vks-meta-pill">🏫 {meta.affiliation}</span>}
        </div>
      </div>

      {/* ── THE BOARD ── */}
      <div className="vks-board-wrap">
        <div className="vks-board-frame" ref={frameRef}>

          {/* board surface */}
          <div className="vks-board" ref={boardRef}>

            {/* Q&A progress timer bar — shown on picture slides only */}
            {!isFullBoardMode(currentSlide, lang) && !qaVisible && (
              <div
                className="vks-qa-timer-bar"
                style={{ width: `${timerWidth}%` }}
              />
            )}

            {/* background image or pure green */}
            <div
              className={`vks-slide-bg ${imgSrc ? "has-image" : "no-image"}`}
              style={imgSrc ? { backgroundImage: `url(${imgSrc})` } : {}}
            />

            {/* chalk dots top-right decoration */}
            <div className="vks-chalk-dots">
              {dotColors.map((c, i) => (
                <div key={i} className="vks-chalk-dot" style={{ background: c }} />
              ))}
            </div>

            {/* top bar */}
            <div className="vks-board-topbar">
              <div className="vks-board-chapter">{currentSlide?.chapter}</div>
              <div className="vks-board-datetime">
                <div>{dateStr}</div>
                <div style={{ fontSize: "1.1em", fontWeight: 700, color: "rgba(249,224,128,0.9)" }}>{timeStr}</div>
                <div className="vks-slide-counter" style={{ textAlign:"right", marginTop:2 }}>
                  Slide {slideIdx + 1} / {totalSlides}
                </div>
              </div>
            </div>

            {/* ── CONTENT OVERLAY ── */}
            <div
              key={animKey}
              className={`vks-overlay ${animDir === "left" ? "vks-slide-enter-left" : "vks-slide-enter"}${fullBoard ? " full-board" : ""}`}
            >
              {/* centered image — only shown when NOT full-board */}
              <div className="vks-center-img" style={{ position: "relative" }}>
                {imgSrc && !fullBoard && (
                  <>
                    <img src={imgSrc} alt="slide" />
                    <button
                      className="vks-zoom-btn"
                      onClick={() => setZoomOpen(true)}
                      title="Zoom image"
                    >⛶</button>
                  </>
                )}
              </div>

              {/* Q&A panel — full board or bottom overlay */}
              {currentSlide && (
                <div className={`vks-qa-panel ${qaVisible ? "qa-visible" : "qa-hidden"}`}>
                  <div className={`vks-q ${isTa ? "vks-tamil" : ""} ${speakPhase === "q" ? "speaking-active" : speakPhase === "a" ? "speaking-dim" : ""}`}>
                    <span className="vks-q-badge">Q</span>
                    <span className="vks-q-text">
                      {qText}
                      {speaking && speakPhase === "q" && (
                        <span className="vks-wave" style={{ marginLeft: 8 }}>
                          <span style={{ background: "#ffe033" }}/><span style={{ background: "#ffe033" }}/><span style={{ background: "#ffe033" }}/><span style={{ background: "#ffe033" }}/><span style={{ background: "#ffe033" }}/>
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={`vks-a ${isTa ? "vks-tamil" : ""} ${speakPhase === "a" ? "speaking-active" : speakPhase === "q" ? "speaking-dim" : ""}`}>
                    <span className="vks-a-badge">A</span>
                    <span className="vks-a-text">
                      {aText}
                      {speaking && speakPhase === "a" && (
                        <span className="vks-wave" style={{ marginLeft: 8 }}>
                          <span style={{ background: "#7ee8fa" }}/><span style={{ background: "#7ee8fa" }}/><span style={{ background: "#7ee8fa" }}/><span style={{ background: "#7ee8fa" }}/><span style={{ background: "#7ee8fa" }}/>
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>

          </div>{/* /board */}

          {/* ── virtualkalvi.com watermark strip ── */}
          <div style={{
            background: "linear-gradient(90deg, #0f2318 0%, #1a3a2a 50%, #0f2318 100%)",
            borderTop: "1px solid rgba(168,216,168,0.12)",
            padding: "4px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <span style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: 11,
              color: "rgba(168,216,168,0.55)",
              letterSpacing: "0.5px",
            }}>
              🎓 virtualkalvi.com
            </span>
            <span style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: 10,
              color: "rgba(249,224,128,0.35)",
              letterSpacing: "0.3px",
            }}>
              Knowledge is Power
            </span>
          </div>

          {/* chalk tray */}
          <div className="vks-chalk-tray" />

        </div>{/* /frame */}
      </div>

      {/* ── SLIDE DOTS + PREV/NEXT ── */}
      <div className="vks-nav">
        <button
          className="vks-nav-btn"
          onClick={prevSlide}
          disabled={isFirst}
          title="Previous"
        >‹</button>

        <div className="vks-slide-dots">
          {currentGroup.map((_, i) => (
            <div
              key={i}
              className={`vks-dot ${i === slideIdx ? "active" : ""}`}
              onClick={() => goToSlide(i, i > slideIdx ? "right" : "left")}
              title={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <button
          className="vks-nav-btn"
          onClick={nextSlide}
          disabled={isLast}
          title="Next"
        >›</button>
      </div>

      {/* ── ACTION BUTTONS ── */}
      <div className="vks-action-bar">
        <button
          className="vks-action-btn vks-btn-speak"
          onClick={() => speakSlide(currentSlide, lang)}
          disabled={speaking}
        >
          {speaking
            ? <><span className="vks-wave"><span/><span/><span/><span/><span/></span> Reading…</>
            : "🔊 Read Aloud"}
        </button>

        {speaking && (
          <button className="vks-action-btn vks-btn-stop" onClick={stopSpeech}>
            ⏹ Stop
          </button>
        )}

        <button
          className={`vks-action-btn vks-btn-auto ${autoPlay ? "running" : ""}`}
          onClick={toggleAutoPlay}
        >
          {autoPlay ? "⏸ Pause Auto" : "▶ Auto Play All"}
        </button>

        {/* ── BOOKMARK current slide ── */}
        <button
          className={`vks-action-btn vks-btn-bookmark ${isBookmarked(topicIdx, slideIdx) ? "bookmarked" : ""}`}
          onClick={toggleBookmark}
          title={isBookmarked(topicIdx, slideIdx) ? "Remove bookmark" : "Bookmark this slide"}
        >
          {isBookmarked(topicIdx, slideIdx) ? "🔖 Bookmarked" : "🔖 Bookmark"}
        </button>

        {/* ── VIEW bookmarks ── */}
        {bookmarks.length > 0 && (
          <button
            className="vks-action-btn vks-btn-bookmark"
            onClick={() => setBookmarkOpen(true)}
            title="View all bookmarks"
            style={{ position: "relative" }}
          >
            📑 Saved
            <span style={{
              position: "absolute", top: -6, right: -4,
              background: "#f9e080", color: "#0a1a12",
              borderRadius: "99px", fontSize: 9, fontWeight: 900,
              padding: "1px 5px", lineHeight: 1.4,
            }}>{bookmarks.length}</span>
          </button>
        )}

        {/* ── EXPORT PDF (bookmarked Q&A only) ── */}
        <button
          className="vks-action-btn vks-btn-pdf"
          onClick={exportPDF}
          disabled={pdfExporting}
          title={bookmarks.length === 0 ? "Bookmark slides first to export PDF" : `Export ${bookmarks.length} bookmarked Q&A as PDF`}
          style={{ position: "relative" }}
        >
          {pdfExporting ? "⏳ Exporting…" : "📄 Export Bookmarks PDF"}
          {bookmarks.length > 0 && !pdfExporting && (
            <span style={{
              position: "absolute", top: -6, right: -4,
              background: "#f87171", color: "#fff",
              borderRadius: "99px", fontSize: 9, fontWeight: 900,
              padding: "1px 5px", lineHeight: 1.4,
            }}>{bookmarks.length}</span>
          )}
        </button>
      </div>

    </div>

      {/* ── SHARE FLOATING PANEL ── */}
      {(() => {
        const pageUrl   = encodeURIComponent(window.location.href);
        const pageTitle = encodeURIComponent(
          brandName + (currentSlide?.chapter ? " — " + currentSlide.chapter : "")
        );
        const waUrl = `https://wa.me/?text=${pageTitle}%20${pageUrl}`;
        return (
          <div className="vks-share-panel">
            <div className="vks-share-label">{capturing ? "⏳" : "Share"}</div>

            {/* WhatsApp — direct link share, no image needed */}
            <a className="vks-share-btn vks-share-wa"
               href={waUrl} target="_blank" rel="noopener noreferrer"
               title="Share on WhatsApp">
              <svg viewBox="0 0 32 32" width="22" height="22" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 2C8.268 2 2 8.268 2 16c0 2.492.648 4.831 1.784 6.865L2 30l7.352-1.762A13.94 13.94 0 0016 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.6a11.56 11.56 0 01-5.888-1.608l-.424-.252-4.36 1.044 1.072-4.248-.276-.436A11.56 11.56 0 014.4 16C4.4 9.592 9.592 4.4 16 4.4S27.6 9.592 27.6 16 22.408 27.6 16 27.6zm6.332-8.672c-.348-.176-2.06-1.016-2.38-1.132-.32-.116-.552-.176-.784.176-.232.348-.9 1.132-1.104 1.368-.2.232-.404.26-.752.088-.348-.176-1.468-.54-2.796-1.724-1.032-.92-1.728-2.056-1.932-2.404-.2-.348-.02-.536.152-.708.156-.156.348-.404.52-.608.176-.2.232-.348.348-.58.116-.232.06-.436-.028-.612-.088-.176-.784-1.892-1.076-2.592-.284-.68-.572-.588-.784-.596l-.668-.012c-.232 0-.608.088-.928.436-.32.348-1.216 1.188-1.216 2.896 0 1.708 1.244 3.356 1.416 3.588.176.232 2.448 3.74 5.932 5.244.828.36 1.476.572 1.98.732.832.264 1.588.228 2.188.14.668-.1 2.06-.844 2.352-1.66.292-.816.292-1.516.204-1.66-.084-.148-.316-.232-.664-.408z"/>
              </svg>
            </a>

            {/* Facebook — downloads slide image + opens sharer with page URL */}
            <button className="vks-share-btn vks-share-fb"
                    onClick={() => captureAndShare("facebook")}
                    disabled={capturing}
                    title="Share on Facebook — slide image will download, then attach it to your post">
              {capturing
                ? <span style={{fontSize:16}}>⏳</span>
                : <svg viewBox="0 0 32 32" width="22" height="22" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path d="M29 16.082C29 9.444 23.627 4 17 4S5 9.444 5 16.082C5 22.123 9.388 27.129 15.125 28v-8.437h-3.047v-3.481h3.047v-2.626c0-3.024 1.792-4.694 4.533-4.694 1.313 0 2.687.235 2.687.235v2.97h-1.514c-1.491 0-1.956.929-1.956 1.882v2.233h3.328l-.532 3.481H18.875V28C24.612 27.129 29 22.123 29 16.082z"/>
                  </svg>
              }
            </button>

            {/* Instagram — downloads slide image + copies link + opens Instagram */}
            <button className="vks-share-btn vks-share-ig"
                    onClick={() => captureAndShare("instagram")}
                    disabled={capturing}
                    title="Share on Instagram — slide image will download &amp; link copied, then paste in your story">
              {capturing
                ? <span style={{fontSize:16}}>⏳</span>
                : <svg viewBox="0 0 32 32" width="22" height="22" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 5.8c3.36 0 3.76.013 5.087.073 1.228.056 1.895.261 2.34.433.587.228 1.006.502 1.447.942.44.441.714.86.942 1.447.172.445.377 1.113.433 2.34.06 1.328.073 1.727.073 5.087s-.013 3.76-.073 5.087c-.056 1.228-.261 1.895-.433 2.34-.228.587-.502 1.006-.942 1.447a3.9 3.9 0 01-1.447.942c-.445.172-1.113.377-2.34.433-1.327.06-1.726.073-5.087.073s-3.76-.013-5.087-.073c-1.228-.056-1.895-.261-2.34-.433a3.9 3.9 0 01-1.447-.942 3.9 3.9 0 01-.942-1.447c-.172-.445-.377-1.113-.433-2.34C5.813 19.76 5.8 19.36 5.8 16s.013-3.76.073-5.087c.056-1.228.261-1.895.433-2.34.228-.587.502-1.006.942-1.447a3.9 3.9 0 011.447-.942c.445-.172 1.113-.377 2.34-.433C12.24 5.813 12.64 5.8 16 5.8M16 4c-3.418 0-3.847.015-5.19.076-1.34.061-2.254.274-3.054.584a6.16 6.16 0 00-2.227 1.45A6.16 6.16 0 004.08 8.336c-.31.8-.523 1.715-.584 3.054C3.435 12.733 3.42 13.162 3.42 16.58c0 3.417.015 3.846.076 5.19.061 1.34.274 2.254.584 3.054a6.16 6.16 0 001.45 2.227 6.16 6.16 0 002.226 1.45c.8.31 1.715.523 3.054.584 1.343.061 1.772.076 5.19.076s3.847-.015 5.19-.076c1.34-.061 2.254-.274 3.054-.584a6.16 6.16 0 002.227-1.45 6.16 6.16 0 001.45-2.227c.31-.8.523-1.715.584-3.054.061-1.344.076-1.773.076-5.19s-.015-3.848-.076-5.19c-.061-1.34-.274-2.255-.584-3.054a6.16 6.16 0 00-1.45-2.227A6.16 6.16 0 0024.244 4.66c-.8-.31-1.715-.523-3.054-.584C19.847 4.015 19.418 4 16 4zm0 5.838a6.162 6.162 0 100 12.324A6.162 6.162 0 0016 9.838zM16 20a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z"/>
                  </svg>
              }
            </button>
          </div>
        );
      })()}

      {/* ── IMAGE ZOOM LIGHTBOX ── */}
      {zoomOpen && imgSrc && (
        <div className="vks-lightbox" onClick={() => setZoomOpen(false)}>
          <button className="vks-lightbox-close" onClick={() => setZoomOpen(false)}>✕</button>
          <img
            className="vks-lightbox-img"
            src={imgSrc}
            alt="slide zoomed"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="vks-lightbox-hint">Click anywhere or press Esc to close</div>
        </div>
      )}

      {/* ── BOOKMARK DRAWER ── */}
      {bookmarkOpen && (
        <div className="vks-modal-backdrop" onClick={() => setBookmarkOpen(false)}>
          <div className="vks-bookmark-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vks-modal-header">
              <div className="vks-modal-title">🔖 Bookmarked Slides</div>
              <button className="vks-modal-close" onClick={() => setBookmarkOpen(false)}>✕</button>
            </div>
            {bookmarks.length === 0 ? (
              <div className="vks-bookmark-empty">No bookmarks yet.<br/>Press 🔖 Bookmark on any slide to save it.</div>
            ) : (
              bookmarks.map((b, i) => (
                <div key={b.key} className="vks-bookmark-item" onClick={() => jumpToBookmark(b)}>
                  <span className="vks-bookmark-num">#{i + 1}</span>
                  <div className="vks-bookmark-content">
                    <div className="vks-bookmark-q">{b.question || "—"}</div>
                    <div className="vks-bookmark-meta">
                      {[b.chapter, b.subject].filter(Boolean).join(" · ")}
                      {" · "}Slide {b.slideIdx + 1}
                    </div>
                  </div>
                  <button
                    className="vks-bookmark-del"
                    title="Remove bookmark"
                    onClick={(e) => { e.stopPropagation(); removeBookmark(b.key); }}
                  >🗑</button>
                </div>
              ))
            )}
            {bookmarks.length > 0 && (
              <button
                onClick={() => {
                  setBookmarks([]);
                  try { localStorage.removeItem("vks_bookmarks"); } catch {}
                }}
                style={{
                  background: "rgba(248,113,113,0.10)",
                  border: "1.5px solid rgba(248,113,113,0.22)",
                  borderRadius: 50, padding: "8px 18px",
                  color: "#f87171", fontFamily: "'Baloo 2',sans-serif",
                  fontSize: 12, fontWeight: 800, cursor: "pointer",
                  alignSelf: "flex-end",
                }}
              >
                🗑 Clear All
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── PDF EXPORT PROGRESS OVERLAY ── */}
      {pdfExporting && (
        <div className="vks-pdf-overlay">
          <div className="vks-pdf-label">📄 Exporting PDF…</div>
          <div className="vks-pdf-progress-wrap">
            <div className="vks-pdf-progress-bar" style={{ width: `${pdfProgress}%` }} />
          </div>
          <div className="vks-pdf-sub">{pdfLabel}</div>
          <div className="vks-pdf-sub" style={{ color: "#f9e080", fontSize: 11 }}>
            Please wait — do not close this tab
          </div>
        </div>
      )}

      {/* ── CONTACT MODAL ── */}
      {contactOpen && (
        <div className="vks-modal-backdrop" onClick={() => setContactOpen(false)}>
          <div className="vks-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vks-modal-header">
              <div className="vks-modal-title">✉️ Contact Us</div>
              <button className="vks-modal-close" onClick={() => setContactOpen(false)}>✕</button>
            </div>

            <div>
              <div className="vks-modal-label">Subject</div>
              <input
                className="vks-modal-input"
                placeholder="e.g. Question about a slide…"
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                disabled={contactSending}
              />
            </div>

            <div>
              <div className="vks-modal-label">Message</div>
              <textarea
                className="vks-modal-textarea"
                placeholder="Write your message here…"
                value={contactMsg}
                onChange={(e) => setContactMsg(e.target.value)}
                disabled={contactSending}
              />
            </div>

            {contactFeedback && (
              <div className={`vks-modal-feedback ${contactFeedback.type}`}>
                {contactFeedback.text}
              </div>
            )}

            <button
              className="vks-modal-send"
              onClick={handleSendContact}
              disabled={contactSending}
            >
              {contactSending
                ? <><span className="vks-wave"><span/><span/><span/><span/><span/></span> Sending…</>
                : "📨 Send Message"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
